import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box, Container, VStack, HStack, Heading, Text, Icon, IconButton, Tooltip, Spinner,
  SimpleGrid, Stat, StatLabel, StatNumber, StatHelpText,
  Tabs, TabList, TabPanels, Tab, TabPanel, Divider, Badge,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer,
  Input, InputGroup, InputLeftElement,
  Select as ChakraSelect,
  Skeleton, SkeletonText, Center,
  Alert, AlertIcon,
  useColorModeValue, useToast, Flex, Avatar,
} from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import {
  FiArrowLeft, FiRefreshCw, FiUsers, FiMessageSquare, FiActivity,
  FiAlertTriangle, FiSearch, FiCalendar, FiTrendingUp, FiChevronUp, FiChevronDown,
} from 'react-icons/fi';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { functionsUsEast1, db } from '../../utils/init-firebase';
import { httpsCallable } from 'firebase/functions';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip as RechartsTooltip,
  ResponsiveContainer, Legend, PieChart, Pie, Cell,
} from 'recharts';
import ReactMarkdown from 'react-markdown';
import { Redirect } from 'react-router-dom';

const DATE_RANGES = [
  { value: 7, label: 'Last 7 Days' },
  { value: 30, label: 'Last 30 Days' },
  { value: 90, label: 'Last 90 Days' },
  { value: 365, label: 'Last Year' },
  { value: 9999, label: 'All Time' },
];

const PIE_COLORS = ['#3182CE', '#38A169', '#E53E3E'];

function KpiCard({ label, value, helpText, icon, color, isLoading }) {
  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const iconBg = useColorModeValue(`${color}.50`, `${color}.900`);

  return (
    <Stat bg={bg} borderWidth="1px" borderColor={borderColor} borderRadius="lg" px={5} py={4}>
      {isLoading ? (
        <VStack align="start" spacing={2}>
          <Skeleton h="14px" w="80px" />
          <Skeleton h="28px" w="60px" />
          <Skeleton h="12px" w="100px" />
        </VStack>
      ) : (
        <>
          <HStack spacing={3} mb={1}>
            <Box p={2} borderRadius="md" bg={iconBg}>
              <Icon as={icon} color={`${color}.500`} boxSize={5} />
            </Box>
            <StatLabel fontSize="sm" color="gray.500">{label}</StatLabel>
          </HStack>
          <StatNumber fontSize="2xl">{value}</StatNumber>
          {helpText && <StatHelpText fontSize="xs" mb={0}>{helpText}</StatHelpText>}
        </>
      )}
    </Stat>
  );
}

function formatDate(isoStr) {
  if (!isoStr) return 'N/A';
  const d = new Date(isoStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatTimestamp(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

function ChatBubble({ message, bgColor, borderColor, textColor, align, avatar }) {
  const metaColor = useColorModeValue('gray.500', 'gray.400');

  return (
    <Flex justify={align === 'right' ? 'flex-end' : 'flex-start'} w="100%">
      <HStack
        align="flex-start"
        spacing={2}
        maxW="80%"
        flexDirection={align === 'right' ? 'row-reverse' : 'row'}
      >
        <Avatar size="xs" name={avatar} mt={1} />
        <Box bg={bgColor} borderWidth="1px" borderColor={borderColor} borderRadius="lg" px={4} py={2} maxW="100%">
          <Box fontSize="sm" color={textColor} className="chat-prose" sx={{ '& p': { m: 0 }, '& p + p': { mt: 2 } }}>
            <ReactMarkdown>{message.message || ''}</ReactMarkdown>
          </Box>
          <Text fontSize="2xs" color={metaColor} mt={1} textAlign={align}>
            {formatTimestamp(message.timestamp?.toDate?.()
              ? message.timestamp.toDate().toISOString()
              : (typeof message.timestamp === 'string' ? message.timestamp : null)
            )}
          </Text>
        </Box>
      </HStack>
    </Flex>
  );
}

function TranscriptViewer({ messages, isLoading }) {
  const containerRef = useRef(null);
  const userBubbleBg = useColorModeValue('blue.50', 'blue.900');
  const userBubbleBorder = useColorModeValue('blue.200', 'blue.700');
  const aiBubbleBg = useColorModeValue('gray.50', 'gray.700');
  const aiBubbleBorder = useColorModeValue('gray.200', 'gray.600');
  const errorBubbleBg = useColorModeValue('red.50', 'red.900');
  const errorBubbleBorder = useColorModeValue('red.200', 'red.700');
  const textColor = useColorModeValue('gray.800', 'gray.100');

  useEffect(() => {
    if (containerRef.current && messages.length > 0) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages]);

  if (isLoading) {
    return (
      <VStack spacing={4} p={4} align="stretch">
        {[1, 2, 3, 4].map((i) => (
          <Flex key={i} justify={i % 2 === 0 ? 'flex-end' : 'flex-start'}>
            <Skeleton borderRadius="lg" h="50px" w="60%" />
          </Flex>
        ))}
      </VStack>
    );
  }

  if (messages.length === 0) {
    return (
      <Center h="200px">
        <Text color="gray.500" fontSize="sm">No messages in this session.</Text>
      </Center>
    );
  }

  return (
    <VStack ref={containerRef} spacing={3} p={4} align="stretch" overflowY="auto" maxH="60vh">
      {messages.map((msg, idx) => {
        const msgType = msg.type || 'unknown';
        if (msgType === 'error') {
          return (
            <ChatBubble
              key={msg.id || idx}
              message={msg}
              bgColor={errorBubbleBg}
              borderColor={errorBubbleBorder}
              textColor={textColor}
              align="left"
              avatar="System"
            />
          );
        }
        if (msgType === 'ai' || msg.user?.uid === 'ai-assistant') {
          return (
            <ChatBubble
              key={msg.id || idx}
              message={msg}
              bgColor={aiBubbleBg}
              borderColor={aiBubbleBorder}
              textColor={textColor}
              align="left"
              avatar="BabyBot"
            />
          );
        }
        return (
          <ChatBubble
            key={msg.id || idx}
            message={msg}
            bgColor={userBubbleBg}
            borderColor={userBubbleBorder}
            textColor={textColor}
            align="right"
            avatar={msg.user?.displayName || 'User'}
          />
        );
      })}
    </VStack>
  );
}

// ─── Overview Tab ───────────────────────────────────────────────────────────

function OverviewTab({ analytics, isLoading }) {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const chartTextColor = useColorModeValue('#4A5568', '#A0AEC0');

  const { totals = {}, dailyVolume = [], typeCounts = {} } = analytics || {};

  const pieData = useMemo(() => {
    if (!typeCounts) return [];
    return [
      { name: 'User', value: typeCounts.user || 0 },
      { name: 'AI', value: typeCounts.ai || 0 },
      { name: 'Error', value: typeCounts.error || 0 },
    ].filter((d) => d.value > 0);
  }, [typeCounts]);

  return (
    <VStack spacing={6} align="stretch">
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
        <KpiCard
          label="Total Users"
          value={totals.totalUsers ?? 0}
          icon={FiUsers}
          color="blue"
          isLoading={isLoading}
        />
        <KpiCard
          label="Total Sessions"
          value={totals.totalSessions ?? 0}
          icon={FiMessageSquare}
          color="purple"
          isLoading={isLoading}
        />
        <KpiCard
          label="Total Messages"
          value={totals.totalMessages?.toLocaleString() ?? '0'}
          icon={FiActivity}
          color="green"
          isLoading={isLoading}
        />
        <KpiCard
          label="Error Rate"
          value={`${totals.errorRate ?? 0}%`}
          icon={FiAlertTriangle}
          color={totals.errorRate > 5 ? 'red' : 'orange'}
          helpText={`${totals.totalErrors ?? 0} errors total`}
          isLoading={isLoading}
        />
      </SimpleGrid>

      <Box bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="xl" p={6}>
        <Heading size="sm" mb={4}>Message Volume Over Time</Heading>
        {isLoading ? (
          <Skeleton h="300px" borderRadius="md" />
        ) : dailyVolume.length === 0 ? (
          <Center h="300px">
            <Text color="gray.500">No message data available for this time range.</Text>
          </Center>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={dailyVolume} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <defs>
                <linearGradient id="colorUser" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3182CE" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3182CE" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorAI" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#38A169" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#38A169" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="date"
                tick={{ fill: chartTextColor, fontSize: 11 }}
                tickFormatter={(v) => {
                  const d = new Date(v + 'T00:00:00');
                  return `${d.getMonth() + 1}/${d.getDate()}`;
                }}
              />
              <YAxis tick={{ fill: chartTextColor, fontSize: 11 }} allowDecimals={false} />
              <RechartsTooltip />
              <Legend />
              <Area type="monotone" dataKey="user" name="User Messages" stroke="#3182CE" fill="url(#colorUser)" stackId="1" />
              <Area type="monotone" dataKey="ai" name="AI Responses" stroke="#38A169" fill="url(#colorAI)" stackId="1" />
              <Area type="monotone" dataKey="error" name="Errors" stroke="#E53E3E" fill="#E53E3E" fillOpacity={0.1} stackId="1" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Box>

      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <Box bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="xl" p={5}>
          <HStack spacing={2} mb={2}>
            <Icon as={FiTrendingUp} color="blue.400" />
            <Text fontWeight="semibold" fontSize="sm">Avg Messages / Session</Text>
          </HStack>
          {isLoading ? <Skeleton h="28px" w="50px" /> : (
            <Text fontSize="2xl" fontWeight="bold">{totals.avgMessagesPerSession ?? 0}</Text>
          )}
        </Box>
        <Box bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="xl" p={5}>
          <HStack spacing={2} mb={2}>
            <Icon as={FiUsers} color="purple.400" />
            <Text fontWeight="semibold" fontSize="sm">Avg Sessions / User</Text>
          </HStack>
          {isLoading ? <Skeleton h="28px" w="50px" /> : (
            <Text fontSize="2xl" fontWeight="bold">{totals.avgSessionsPerUser ?? 0}</Text>
          )}
        </Box>
        <Box bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="xl" p={5}>
          <HStack spacing={2} mb={2}>
            <Icon as={FiCalendar} color="green.400" />
            <Text fontWeight="semibold" fontSize="sm">Busiest Day</Text>
          </HStack>
          {isLoading ? <Skeleton h="28px" w="80px" /> : (
            <Text fontSize="2xl" fontWeight="bold">{totals.busiestDay ?? 'N/A'}</Text>
          )}
        </Box>
      </SimpleGrid>

      {pieData.length > 0 && (
        <Box bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="xl" p={6}>
          <Heading size="sm" mb={4}>Message Type Breakdown</Heading>
          {isLoading ? <Skeleton h="200px" borderRadius="md" /> : (
            <Center>
              <ResponsiveContainer width={300} height={220}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={3} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                    {pieData.map((_, i) => (
                      <Cell key={`cell-${i}`} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            </Center>
          )}
        </Box>
      )}
    </VStack>
  );
}

// ─── Users Tab ──────────────────────────────────────────────────────────────

function UsersTab({ users, isLoading, onSelectUser }) {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('lastActive');
  const [sortDir, setSortDir] = useState('desc');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const headerBg = useColorModeValue('gray.50', 'gray.750');

  const filtered = useMemo(() => {
    let list = users || [];
    if (search.trim()) {
      const term = search.toLowerCase();
      list = list.filter(
        (u) =>
          (u.displayName || '').toLowerCase().includes(term) ||
          (u.email || '').toLowerCase().includes(term)
      );
    }
    list = [...list].sort((a, b) => {
      let aVal = a[sortKey];
      let bVal = b[sortKey];
      if (sortKey === 'lastActive') {
        aVal = aVal || '';
        bVal = bVal || '';
      }
      if (typeof aVal === 'string') {
        return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDir === 'asc' ? (aVal ?? 0) - (bVal ?? 0) : (bVal ?? 0) - (aVal ?? 0);
    });
    return list;
  }, [users, search, sortKey, sortDir]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return null;
    return <Icon as={sortDir === 'asc' ? FiChevronUp : FiChevronDown} ml={1} boxSize={3} />;
  };

  if (isLoading) {
    return (
      <VStack spacing={3} align="stretch">
        <Skeleton h="40px" borderRadius="md" />
        {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} h="45px" borderRadius="md" />)}
      </VStack>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      <InputGroup>
        <InputLeftElement pointerEvents="none">
          <Icon as={FiSearch} color="gray.400" />
        </InputLeftElement>
        <Input
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </InputGroup>

      <TableContainer>
        <Table variant="simple" size="sm">
          <Thead>
            <Tr>
              <Th cursor="pointer" onClick={() => handleSort('displayName')} bg={headerBg}>
                Name <SortIcon col="displayName" />
              </Th>
              <Th cursor="pointer" onClick={() => handleSort('email')} bg={headerBg}>
                Email <SortIcon col="email" />
              </Th>
              <Th cursor="pointer" onClick={() => handleSort('messageCount')} isNumeric bg={headerBg}>
                Messages <SortIcon col="messageCount" />
              </Th>
              <Th cursor="pointer" onClick={() => handleSort('sessionCount')} isNumeric bg={headerBg}>
                Sessions <SortIcon col="sessionCount" />
              </Th>
              <Th cursor="pointer" onClick={() => handleSort('lastActive')} bg={headerBg}>
                Last Active <SortIcon col="lastActive" />
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {filtered.map((u) => (
              <Tr
                key={u.uid}
                cursor="pointer"
                onClick={() => onSelectUser(u)}
                _hover={{ bg: hoverBg }}
              >
                <Td fontWeight="medium">{u.displayName || 'Unknown'}</Td>
                <Td fontSize="sm" color="gray.500">{u.email || '--'}</Td>
                <Td isNumeric>{u.messageCount}</Td>
                <Td isNumeric>{u.sessionCount}</Td>
                <Td fontSize="sm">{formatDate(u.lastActive)}</Td>
              </Tr>
            ))}
            {filtered.length === 0 && (
              <Tr>
                <Td colSpan={5}>
                  <Center py={4}>
                    <Text color="gray.500" fontSize="sm">
                      {search ? 'No users match your search.' : 'No chatbot users found.'}
                    </Text>
                  </Center>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>
      <Text fontSize="xs" color="gray.500">{filtered.length} user{filtered.length !== 1 ? 's' : ''} shown</Text>
    </VStack>
  );
}

// ─── Conversations Tab ──────────────────────────────────────────────────────

function ConversationsTab({ users, preSelectedUser }) {
  const [selectedUser, setSelectedUser] = useState(preSelectedUser || null);
  const [sessions, setSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const selectedBg = useColorModeValue('blue.50', 'blue.900');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const previewColor = useColorModeValue('gray.700', 'gray.300');
  const toast = useToast();

  useEffect(() => {
    if (preSelectedUser) setSelectedUser(preSelectedUser);
  }, [preSelectedUser]);

  const userOptions = useMemo(
    () =>
      (users || []).map((u) => ({
        value: u.uid,
        label: `${u.displayName || 'Unknown'} (${u.email || u.uid})`,
      })),
    [users]
  );

  const selectedOption = useMemo(
    () => userOptions.find((o) => o.value === selectedUser?.uid) || null,
    [userOptions, selectedUser]
  );

  const fetchSessions = useCallback(async (uid) => {
    if (!uid) return;
    setLoadingSessions(true);
    setSessions([]);
    setSelectedSessionId(null);
    setMessages([]);
    try {
      const q = query(
        collection(db, 'users', uid, 'aichatlog'),
        orderBy('timestamp', 'desc')
      );
      const snap = await getDocs(q);
      const sessionMap = {};
      snap.docs.forEach((doc) => {
        const d = doc.data();
        const sid = d.sessionId;
        if (!sid) return;
        if (!sessionMap[sid]) {
          sessionMap[sid] = {
            sessionId: sid,
            messageCount: 0,
            firstMessage: null,
            lastTimestamp: null,
            preview: null,
          };
        }
        const s = sessionMap[sid];
        s.messageCount++;
        const ts = d.timestamp?.toDate?.() ?? null;
        if (ts && (!s.lastTimestamp || ts > s.lastTimestamp)) s.lastTimestamp = ts;
        if (d.type === 'user' && !s.preview) {
          s.preview = (d.message || '').slice(0, 80);
        }
      });
      const sorted = Object.values(sessionMap).sort(
        (a, b) => (b.lastTimestamp || 0) - (a.lastTimestamp || 0)
      );
      setSessions(sorted);
    } catch (err) {
      console.error('Error fetching sessions:', err);
      toast({ title: 'Error loading sessions', status: 'error', duration: 3000 });
    } finally {
      setLoadingSessions(false);
    }
  }, [toast]);

  useEffect(() => {
    if (selectedUser?.uid) {
      fetchSessions(selectedUser.uid);
    } else {
      setSessions([]);
      setSelectedSessionId(null);
      setMessages([]);
    }
  }, [selectedUser, fetchSessions]);

  const fetchMessages = useCallback(async (uid, sessionId) => {
    if (!uid || !sessionId) return;
    setLoadingMessages(true);
    setMessages([]);
    try {
      const q = query(
        collection(db, 'users', uid, 'aichatlog'),
        where('sessionId', '==', sessionId),
        orderBy('timestamp', 'asc')
      );
      const snap = await getDocs(q);
      setMessages(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    } catch (err) {
      console.error('Error fetching messages:', err);
      toast({ title: 'Error loading messages', status: 'error', duration: 3000 });
    } finally {
      setLoadingMessages(false);
    }
  }, [toast]);

  useEffect(() => {
    if (selectedUser?.uid && selectedSessionId) {
      fetchMessages(selectedUser.uid, selectedSessionId);
    } else {
      setMessages([]);
    }
  }, [selectedUser, selectedSessionId, fetchMessages]);

  return (
    <VStack spacing={4} align="stretch">
      <Box>
        <Text fontSize="sm" fontWeight="medium" mb={2}>Select a user</Text>
        <Select
          placeholder="Search for a user..."
          options={userOptions}
          value={selectedOption}
          onChange={(opt) => {
            const user = (users || []).find((u) => u.uid === opt?.value) || null;
            setSelectedUser(user);
          }}
          isClearable
          useBasicStyles
        />
      </Box>

      {!selectedUser && (
        <Center py={10}>
          <VStack spacing={2}>
            <Icon as={FiMessageSquare} boxSize={10} color="gray.400" />
            <Text color="gray.500" fontSize="sm">Select a user above to browse their conversations.</Text>
          </VStack>
        </Center>
      )}

      {selectedUser && (
        <Flex
          direction={{ base: 'column', md: 'row' }}
          gap={4}
          minH="400px"
        >
          <Box
            w={{ base: '100%', md: '320px' }}
            flexShrink={0}
            bg={cardBg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="xl"
            overflow="hidden"
          >
            <Box px={4} py={3} borderBottomWidth="1px" borderColor={borderColor}>
              <HStack justify="space-between">
                <Text fontWeight="semibold" fontSize="sm">Sessions</Text>
                <Badge colorScheme="blue" fontSize="xs">{sessions.length}</Badge>
              </HStack>
            </Box>
            <Box maxH="55vh" overflowY="auto">
              {loadingSessions ? (
                <VStack p={4} spacing={3}>
                  {[1, 2, 3].map((i) => <Skeleton key={i} h="60px" borderRadius="md" />)}
                </VStack>
              ) : sessions.length === 0 ? (
                <Center py={8}>
                  <Text color="gray.500" fontSize="sm">No sessions found.</Text>
                </Center>
              ) : (
                sessions.map((s) => (
                  <Box
                    key={s.sessionId}
                    px={4}
                    py={3}
                    cursor="pointer"
                    bg={selectedSessionId === s.sessionId ? selectedBg : 'transparent'}
                    _hover={{ bg: selectedSessionId === s.sessionId ? selectedBg : hoverBg }}
                    borderBottomWidth="1px"
                    borderColor={borderColor}
                    onClick={() => setSelectedSessionId(s.sessionId)}
                  >
                    <HStack justify="space-between" mb={1}>
                      <Text fontSize="xs" color="gray.500">
                        {s.lastTimestamp ? formatTimestamp(s.lastTimestamp.toISOString()) : 'Unknown'}
                      </Text>
                      <Badge size="sm" colorScheme="gray" fontSize="2xs">
                        {s.messageCount} msg{s.messageCount !== 1 ? 's' : ''}
                      </Badge>
                    </HStack>
                    <Text fontSize="sm" noOfLines={2} color={previewColor}>
                      {s.preview || '(No preview)'}
                    </Text>
                  </Box>
                ))
              )}
            </Box>
          </Box>

          <Box
            flex={1}
            bg={cardBg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="xl"
            overflow="hidden"
            display="flex"
            flexDirection="column"
          >
            <Box px={4} py={3} borderBottomWidth="1px" borderColor={borderColor}>
              <Text fontWeight="semibold" fontSize="sm">
                {selectedSessionId
                  ? `Conversation (${messages.length} messages)`
                  : 'Select a session'}
              </Text>
            </Box>
            {!selectedSessionId ? (
              <Center flex={1} py={10}>
                <Text color="gray.500" fontSize="sm">Pick a session from the left to view the conversation.</Text>
              </Center>
            ) : (
              <TranscriptViewer messages={messages} isLoading={loadingMessages} />
            )}
          </Box>
        </Flex>
      )}
    </VStack>
  );
}

// ─── Main Page ──────────────────────────────────────────────────────────────

const ChatbotAnalyticsPage = () => {
  const { currentUser, loading: authLoading, SuperAdmin } = useAuth();
  const history = useHistory();
  const toast = useToast();

  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [daysBack, setDaysBack] = useState(90);
  const [tabIndex, setTabIndex] = useState(0);
  const [conversationUser, setConversationUser] = useState(null);

  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const headerBG = useColorModeValue('white', 'gray.800');
  const boxBG = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const fn = httpsCallable(functionsUsEast1, 'getChatbotAnalytics');
      const result = await fn({ daysBack });
      setAnalytics(result.data);
    } catch (err) {
      console.error('getChatbotAnalytics error:', err);
      setError(err.message || 'Failed to load analytics.');
      toast({ title: 'Error loading analytics', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setLoading(false);
    }
  }, [daysBack, toast]);

  useEffect(() => {
    if (SuperAdmin) fetchAnalytics();
  }, [SuperAdmin, fetchAnalytics]);

  const handleSelectUser = useCallback((user) => {
    setConversationUser(user);
    setTabIndex(2);
  }, []);

  if (authLoading) {
    return (
      <Center minH="100vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (!SuperAdmin) {
    return <Redirect to="/" />;
  }

  return (
    <Box bg={bgColor} minH="100vh">
      <Container maxW="1600px" py={8}>
        <VStack align="stretch" spacing={6}>
          <Box bg={headerBG} p={6} borderRadius="xl" shadow="sm" borderWidth="1px" borderColor={borderColor}>
            <Flex
              direction={{ base: 'column', md: 'row' }}
              align={{ base: 'flex-start', md: 'center' }}
              justify="space-between"
              gap={4}
            >
              <HStack spacing={3}>
                <Tooltip label="Back">
                  <IconButton
                    icon={<FiArrowLeft />}
                    variant="ghost"
                    size="sm"
                    onClick={() => history.goBack()}
                    aria-label="Go back"
                  />
                </Tooltip>
                <Box>
                  <Heading size="lg">BabyBot Analytics</Heading>
                  <Text fontSize="sm" color="gray.500">AI chatbot usage insights</Text>
                </Box>
              </HStack>
              <HStack spacing={3}>
                <ChakraSelect
                  size="sm"
                  w="160px"
                  value={daysBack}
                  onChange={(e) => setDaysBack(Number(e.target.value))}
                >
                  {DATE_RANGES.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </ChakraSelect>
                <Tooltip label="Refresh">
                  <IconButton
                    icon={<FiRefreshCw />}
                    variant="outline"
                    size="sm"
                    onClick={fetchAnalytics}
                    isLoading={loading}
                    aria-label="Refresh data"
                  />
                </Tooltip>
              </HStack>
            </Flex>
          </Box>

          {error && (
            <Alert status="error" borderRadius="lg">
              <AlertIcon />
              {error}
            </Alert>
          )}

          <Box bg={boxBG} borderRadius="xl" shadow="sm" borderWidth="1px" borderColor={borderColor}>
            <Tabs
              colorScheme="blue"
              isLazy
              index={tabIndex}
              onChange={setTabIndex}
            >
              <TabList px={6} pt={2}>
                <Tab>Overview</Tab>
                <Tab>Users {analytics?.users?.length > 0 && <Badge ml={2} colorScheme="blue" fontSize="xs">{analytics.users.length}</Badge>}</Tab>
                <Tab>Conversations</Tab>
              </TabList>
              <Divider />
              <TabPanels>
                <TabPanel px={6} py={6}>
                  <OverviewTab analytics={analytics} isLoading={loading} />
                </TabPanel>
                <TabPanel px={6} py={6}>
                  <UsersTab
                    users={analytics?.users || []}
                    isLoading={loading}
                    onSelectUser={handleSelectUser}
                  />
                </TabPanel>
                <TabPanel px={6} py={6}>
                  <ConversationsTab
                    users={analytics?.users || []}
                    preSelectedUser={conversationUser}
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default ChatbotAnalyticsPage;
